import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { InterviewService } from '@/services/interview/interviewService';
import { toast } from 'sonner';

export const useDeleteInterview = () => {
  const queryClient = useQueryClient();
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [interviewToDelete, setInterviewToDelete] = useState<string | null>(null);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);

  // Open the confirmation dialog for deleting
  const confirmDelete = (interviewId: string) => {
    setInterviewToDelete(interviewId);
    setIsConfirmDialogOpen(true);
  };

  const handleDelete = async (interviewId: string) => {
    if (processingId) {
      console.log('Behandler allerede et annet intervju, hopper over');
      return;
    }
    
    try {
      setProcessingId(interviewId);
      toast.loading('Sletter intervju...', { id: 'delete' });
      
      console.log('Attempting to delete interview:', interviewId);
      const result = await InterviewService.deleteInterview(interviewId);
      
      if (result) {
        console.log('Delete successful, invalidating queries');
        
        // Force an immediate invalidation of ALL relevant queries
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['pendingInterviews'] }),
          queryClient.invalidateQueries({ queryKey: ['publishedInterviews'] }),
          queryClient.invalidateQueries({ queryKey: ['publishedInterviews', 'paginated'] }),
          queryClient.invalidateQueries({ queryKey: ['publishedInterviews', 'infinite'] }),
          queryClient.invalidateQueries({ queryKey: ['feederInterviews'] }),
          queryClient.invalidateQueries({ queryKey: ['interviews'] })
        ]);
        
        toast.success('Intervju slettet', { id: 'delete' });
        
        // Wait a moment to ensure database consistency
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Force a direct refetch to ensure latest data
        await Promise.all([
          queryClient.refetchQueries({ queryKey: ['pendingInterviews'] }),
          queryClient.refetchQueries({ queryKey: ['publishedInterviews'] }),
          queryClient.refetchQueries({ queryKey: ['publishedInterviews', 'paginated'] }),
          queryClient.refetchQueries({ queryKey: ['publishedInterviews', 'infinite'] }),
          queryClient.refetchQueries({ queryKey: ['feederInterviews'] }),
          queryClient.refetchQueries({ queryKey: ['interviews'] })
        ]);
      } else {
        toast.error('Kunne ikke slette intervju', { id: 'delete' });
        throw new Error('Kunne ikke slette intervju');
      }
    } catch (error) {
      console.error('Feil ved sletting:', error);
      toast.error(`Kunne ikke slette intervju: ${error instanceof Error ? error.message : 'Ukjent feil'}`, { id: 'delete' });
    } finally {
      setTimeout(() => setProcessingId(null), 500);
    }
  };

  const closeConfirmDialog = () => {
    setIsConfirmDialogOpen(false);
    setInterviewToDelete(null);
  };

  const confirmAndDelete = () => {
    if (interviewToDelete) {
      handleDelete(interviewToDelete);
      setInterviewToDelete(null);
    }
    setIsConfirmDialogOpen(false);
  };

  return {
    processingId,
    isConfirmDialogOpen,
    interviewToDelete,
    confirmDelete,
    handleDelete,
    closeConfirmDialog,
    confirmAndDelete
  };
};
